/**
 * Paper odds from our own recorded history ("historic causality").
 *
 *  (A) sigma_f: a HAR-style volatility forecast in log space from the 16 s EWMA, the
 *      600 s h10 estimator and an hour-of-day x weekend baseline, all fitted on the market log.
 *  (B) a calibration map (|band offset|, lead-time bucket, logit p_model) -> realized touch
 *      frequency, shrunk toward the model: p_cal = (hits + N0 * mean p_model) / (n + N0).
 *  (C) pay-less-only: p_used = max(p_live(sigma_EWMA, guard-free), p_cal). The paper house never
 *      pays more on a cell than the deployed EWMA model would; cells above the cap are hidden.
 *
 * The map and coefficients come from tooling/calibrate/fit-paper.mjs as a hashed JSON file.
 * Settlement is untouched: this module only prices.
 */
import { floorGrossHundredths } from './model.js';
import { linearBandProbability } from './linear-probability.js';
export const HIST_CAL_MODEL_ID = 'maya-gaussian-linear-six-v2+hist-cal-v1';
export const CALIBRATION_SCHEMA = 'maya-paper-calibration/1';
/**
 * Lead-time buckets [3,8) [8,13) ... [28,36). A quote from a fresh sample puts the sixth window at up to 33 s; the
 * source sample is ~0.8 s old when quoted (p50, max ~1.5 s) and the clock adds its uncertainty, so the recorder logged
 * the sixth window at 34 s in 8 % of rows (Oct 2026). The last bucket therefore extends to 36 s.
 */
export const TAU_EDGES = Object.freeze([3, 8, 13, 18, 23, 28, 36]);
/** Right-closed logit(p_model) bins (a, b]: five coarse bins for cells the Gaussian model calls impossible, then 20 over (-7, 3]. */
export const LOGIT_EDGES = Object.freeze([-40, -20, -14, -10, -8.5, ...Array.from({ length: 21 }, (_, i) => -7 + i * 0.5)]);
export const MAX_ABS_OFFSET = 3;
export const EWMA_WINDOW_SAMPLES = 68;
export const H600_WINDOW_MS = 600_000;
export const H600_STEP_MS = 10_000;
export const H600_MIN_RETURNS = 30;
/** Samples the quote book keeps for the h600 estimator: 600 s of 10 s returns need 610 s of path. */
export const LONG_HISTORY_MS = H600_WINDOW_MS + H600_STEP_MS;
export const PROBABILITY_CLIP = Object.freeze([1e-16, 1 - 1e-9]);
export const RETURN_GATE = Object.freeze({ overallLow: 0.8, overallHigh: 1, bucketLow: 0.7, bucketHigh: 1.15, minN: 500, minHits: 100 });
/** Multiplier buckets (x) for return reports: [1,1.1) [1.1,1.25) ... [50,inf). */
export const MULTIPLIER_BUCKETS = Object.freeze([1, 1.1, 1.25, 1.5, 2, 3, 5, 10, 20, 50, Infinity]);
const TAU_BUCKETS = TAU_EDGES.length - 1;
const LOGIT_BINS = LOGIT_EDGES.length - 1;
const finite = (v) => typeof v === 'number' && Number.isFinite(v);
const compactTime = (iso) => iso.replace(/[-:]/g, '').replace(/\.\d+/, '').replace(/(T\d{4})\d{2}Z$/, '$1Z');
/** UTC hour-of-day x weekend bin (Saturday/Sunday = weekend), 0..47. */
export function baselineBin(sourceTimeMs) {
    const date = new Date(sourceTimeMs);
    const day = date.getUTCDay();
    return (day === 0 || day === 6 ? 24 : 0) + date.getUTCHours();
}
/**
 * The live house's h10 estimator: RMS of 10 s log returns over the trailing 600 s, divided by sqrt(10).
 * Only pairs exactly 10 s apart with both ends present count; fewer than 30 returns -> undefined.
 */
export function h600Sigma(points, latestMs) {
    let sum = 0, count = 0;
    for (let t = latestMs; t > latestMs - H600_WINDOW_MS; t -= 1000) {
        const v = points.get(t), u = points.get(t - H600_STEP_MS);
        if (v === undefined || u === undefined || !(v > 0 && u > 0))
            continue;
        const r = Math.log(v / u);
        sum += r * r;
        count++;
    }
    return count >= H600_MIN_RETURNS ? Math.sqrt(sum / count / (H600_STEP_MS / 1000)) : undefined;
}
/** (A) sigma_f = k * exp(c + a ln sigma_EWMA + b ln sigma_h600 + d ln B). 0 when an input is missing or not positive. */
export function forecastSigma(forecast, sigmaEwma, sigmaH600, sourceTimeMs) {
    if (!(sigmaEwma > 0) || !(sigmaH600 !== undefined && sigmaH600 > 0))
        return 0;
    const base = forecast.baseline[baselineBin(sourceTimeMs)];
    const B = base !== undefined && base > 0 ? base : forecast.baselineAll;
    const value = forecast.k * Math.exp(forecast.c + forecast.a * Math.log(sigmaEwma) + forecast.b * Math.log(sigmaH600) + forecast.d * Math.log(B));
    return Number.isFinite(value) && value > 0 ? value : 0;
}
/**
 * The Gaussian linear-path touch probability without the touch table's domain guard.
 * If the quadrature cannot be evaluated (zero or extreme sigma) the limiting value is used:
 * 1 when the band contains spot, else 0.
 */
export function guardFreeProbability(spot, lower, upper, timeToStartS, sigma) {
    try {
        const scale = sigma * Math.sqrt(timeToStartS);
        return linearBandProbability({ zLower: Math.log(lower / spot) / scale, zUpper: Math.log(upper / spot) / scale, timeToStartS, windowS: 5, observationIntervalS: 1 }).probability;
    }
    catch {
        return lower <= spot && spot <= upper ? 1 : 0;
    }
}
export function clippedLogit(p) {
    const q = Math.min(PROBABILITY_CLIP[1], Math.max(PROBABILITY_CLIP[0], p));
    return Math.log(q / (1 - q));
}
export function tauBucket(timeToStartS) {
    for (let i = 0; i < TAU_BUCKETS; i++)
        if (timeToStartS >= TAU_EDGES[i] && timeToStartS < TAU_EDGES[i + 1])
            return i;
    return undefined;
}
export function logitBin(pModel) {
    const x = clippedLogit(pModel);
    for (let i = 0; i < LOGIT_BINS; i++)
        if (x > LOGIT_EDGES[i] && x <= LOGIT_EDGES[i + 1])
            return i;
    return undefined;
}
export function cellKey(absOffset, tau, bin) { return (absOffset * TAU_BUCKETS + tau) * LOGIT_BINS + bin; }
/** True when the map has an opinion about this lead time and offset (independent of observations). */
export function calibrationCovers(absOffset, timeToStartS) {
    return Number.isInteger(absOffset) && absOffset >= 0 && absOffset <= MAX_ABS_OFFSET && tauBucket(timeToStartS) !== undefined;
}
/** Map key for a cell, or undefined outside the map (|b| > 3, lead time outside [3, 34) s, logit above 3). */
export function calibrationKey(absOffset, timeToStartS, pModel) {
    if (!calibrationCovers(absOffset, timeToStartS))
        return undefined;
    const tau = tauBucket(timeToStartS), bin = logitBin(pModel);
    return bin === undefined ? undefined : { key: cellKey(absOffset, tau, bin), tau, bin };
}
/**
 * (B)+(C) for one cell. Never returns a probability below the live EWMA model's guard-free one,
 * so the multiplier is never above what that model would pay.
 */
export function previewCalibrated(calibration, p) {
    const numbers = [p.spot, p.lower, p.upper, p.timeToStartS, p.widthS, p.sigmaPerSqrtSec, p.settleDtS, p.houseEdge, p.minimumGross, p.sigmaForecastPerSqrtSec, p.bandOffset];
    if (!numbers.every(Number.isFinite) || p.spot <= 0 || p.lower <= 0 || p.upper <= p.lower || p.timeToStartS <= 0 || p.widthS !== 5 || p.settleDtS !== 1 || p.houseEdge < 0 || p.houseEdge >= 1 || p.minimumGross < 1 || !Number.isInteger(p.bandOffset) || p.calibrationId !== calibration.calibrationId)
        return { available: false, reason: 'invalid-input' };
    if (p.sigmaPerSqrtSec < 0 || !(p.sigmaForecastPerSqrtSec > 0))
        return { available: false, reason: 'invalid-volatility' };
    const minimum = BigInt(Math.ceil(p.minimumGross * 100));
    const pLive = guardFreeProbability(p.spot, p.lower, p.upper, p.timeToStartS, p.sigmaPerSqrtSec);
    // p_used >= p_live, so a cell the live model already prices below the minimum stays there.
    if (pLive > 0 && floorGrossHundredths(pLive, p.houseEdge) < minimum)
        return { available: false, reason: 'below-minimum' };
    const pModel = guardFreeProbability(p.spot, p.lower, p.upper, p.timeToStartS, p.sigmaForecastPerSqrtSec);
    const key = calibrationKey(Math.abs(p.bandOffset), p.timeToStartS, pModel);
    if (!key)
        return { available: false, reason: 'out-of-domain' };
    const coordinates = [key.tau, Math.abs(p.bandOffset), key.bin];
    const cell = calibration.cells.get(key.key);
    if (!cell || cell.n < calibration.minObservations)
        return { available: false, reason: 'out-of-domain', coordinates };
    const probability = Math.max(pLive, cell.pCal);
    if (!(probability > 0 && probability <= 1))
        return { available: false, reason: 'unreachable', coordinates };
    let gross;
    try {
        gross = floorGrossHundredths(probability, p.houseEdge);
    }
    catch {
        return { available: false, reason: 'unsafe-numeric-range', coordinates };
    }
    if (gross < minimum)
        return { available: false, reason: 'below-minimum', coordinates };
    if (gross > calibration.capX100)
        return { available: false, reason: 'hidden-above-cap', coordinates };
    return { available: true, probability, grossX100: gross.toString(), coordinates, source: cell.pCal > pLive ? 'calibrated' : 'live', pLive, pModel, pCal: cell.pCal };
}
export function canonicalCalibrationBody(file) {
    const { contentHash: _ignored, ...body } = file;
    return JSON.stringify(body);
}
export async function sha256Hex(text) {
    const digest = await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
    return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}
/** The map's own holdout return must be in [overallLow, overallHigh] overall and in [bucketLow, bucketHigh] in every bucket with n >= minN and hits >= minHits. */
export function gateFailures(report, gate = RETURN_GATE) {
    const out = [];
    if (!(report.overallReturn >= gate.overallLow && report.overallReturn <= gate.overallHigh))
        out.push(`overall: return ${report.overallReturn.toFixed(3)}`);
    for (const b of report.buckets) {
        const hits = Math.round(b.realized * b.n);
        if (b.n >= gate.minN && hits >= gate.minHits && !(b.return >= gate.bucketLow && b.return <= gate.bucketHigh))
            out.push(`${b.lo}-${b.hi ?? 'inf'}x: return ${b.return.toFixed(3)} on n=${b.n}, hits=${hits}`);
    }
    return out;
}
/**
 * Structural validation; the hash is checked by verifyCalibration. Throws on any inconsistency.
 * `requireGate: false` exists only for the fitter's own out-of-sample pass, before the gate result exists.
 */
export function buildCalibration(value, expectedMarketId, { requireGate = true } = {}) {
    if (!value || typeof value !== 'object')
        throw new Error('Calibration file is not an object');
    const f = value;
    if (f.schema !== CALIBRATION_SCHEMA)
        throw new Error('Unsupported calibration schema');
    if (f.modelId !== HIST_CAL_MODEL_ID)
        throw new Error('Calibration model id mismatch');
    if (typeof f.marketId !== 'string' || (expectedMarketId !== undefined && f.marketId !== expectedMarketId))
        throw new Error('Calibration market mismatch');
    if (typeof f.contentHash !== 'string' || !/^sha256:[0-9a-f]{64}$/.test(f.contentHash))
        throw new Error('Calibration content hash missing');
    const fit = f.fit, fc = f.forecast, map = f.map;
    if (!fit || typeof fit.logFrom !== 'string' || typeof fit.logTo !== 'string' || !Number.isFinite(Date.parse(fit.logFrom)) || !Number.isFinite(Date.parse(fit.logTo)))
        throw new Error('Calibration fit window missing');
    if (!fc || ![fc.c, fc.a, fc.b, fc.d, fc.k, fc.baselineAll].every(finite) || !(fc.k > 0) || !(fc.baselineAll > 0) || !Array.isArray(fc.baseline) || fc.baseline.length !== 48 || !fc.baseline.every((v) => finite(v) && v >= 0))
        throw new Error('Invalid calibration forecast');
    if (!map || JSON.stringify(map.tauEdges) !== JSON.stringify(TAU_EDGES) || JSON.stringify(map.logitEdges) !== JSON.stringify(LOGIT_EDGES) || map.maxAbsOffset !== MAX_ABS_OFFSET)
        throw new Error('Calibration bins do not match this build');
    if (!finite(map.n0) || map.n0 < 0 || !Number.isInteger(map.minObservations) || map.minObservations < 30 || !Number.isInteger(map.capX100) || map.capX100 < 101 || map.capX100 > 5000)
        throw new Error('Invalid calibration offer rules');
    if (JSON.stringify(map.columns) !== JSON.stringify(['absOffset', 'tauBucket', 'logitBin', 'hits', 'n', 'meanPModel']) || !Array.isArray(map.cells) || map.cells.length > (MAX_ABS_OFFSET + 1) * TAU_BUCKETS * LOGIT_BINS)
        throw new Error('Invalid calibration map');
    const cells = new Map();
    for (const row of map.cells) {
        if (!Array.isArray(row) || row.length !== 6 || !row.every(finite))
            throw new Error('Invalid calibration map row');
        const [absOffset, tau, bin, hits, n, meanPModel] = row;
        if (![absOffset, tau, bin, hits, n].every(Number.isInteger) || absOffset < 0 || absOffset > MAX_ABS_OFFSET || tau < 0 || tau >= TAU_BUCKETS || bin < 0 || bin >= LOGIT_BINS || n < 1 || hits < 0 || hits > n || !(meanPModel >= 0 && meanPModel <= 1))
            throw new Error('Invalid calibration map row');
        const key = cellKey(absOffset, tau, bin);
        if (cells.has(key))
            throw new Error('Duplicate calibration map row');
        // Keys below the observation minimum are kept in the file for transparency but never priced.
        cells.set(key, Object.freeze({ hits, n, meanPModel, pCal: (hits + map.n0 * meanPModel) / (n + map.n0) }));
    }
    if (requireGate) {
        const gate = f.report?.gate, holdout = f.report?.holdout;
        if (!gate || !holdout?.calibrated || !Array.isArray(holdout.calibrated.buckets))
            throw new Error('Calibration out-of-sample report missing');
        for (const k of Object.keys(RETURN_GATE))
            if (gate[k] !== RETURN_GATE[k])
                throw new Error('Calibration acceptance gate differs from this build');
        const failures = gateFailures(holdout.calibrated, gate);
        if (!gate.passed || failures.length)
            throw new Error(`Calibration failed its out-of-sample acceptance gate: ${failures.join('; ') || 'marked failed'}`);
    }
    const hash = f.contentHash.slice('sha256:'.length);
    const calibrationId = `hist-cal-v1:${f.marketId}:${compactTime(fit.logFrom)}-${compactTime(fit.logTo)}:${hash.slice(0, 12)}`;
    return Object.freeze({
        calibrationId, marketId: f.marketId, modelId: f.modelId, bandHeight: String(f.bandHeight), fitWindow: Object.freeze({ from: fit.logFrom, to: fit.logTo }), contentHash: f.contentHash,
        forecast: Object.freeze({ c: fc.c, a: fc.a, b: fc.b, d: fc.d, k: fc.k, baseline: Object.freeze([...fc.baseline]), baselineAll: fc.baselineAll }),
        n0: map.n0, minObservations: map.minObservations, capX100: BigInt(map.capX100), cells, file: f,
    });
}
/** Parses, checks the content hash against the canonical body, then validates the structure and gate. */
export async function verifyCalibration(text, expectedMarketId) {
    let value;
    try {
        value = JSON.parse(text);
    }
    catch {
        throw new Error('Calibration file is not JSON');
    }
    const file = value;
    if (!file || typeof file !== 'object' || typeof file.contentHash !== 'string')
        throw new Error('Calibration content hash missing');
    const actual = `sha256:${await sha256Hex(canonicalCalibrationBody(file))}`;
    if (actual !== file.contentHash)
        throw new Error('Calibration content hash mismatch');
    return buildCalibration(file, expectedMarketId);
}
//# sourceMappingURL=calibration.js.map